import { useState } from 'react';
import { useAdminData } from '../context/AdminDataContext';
import {
  Plus, Search, Edit2, Trash2, X, ChevronLeft, ChevronRight,
  Building2, Star, Check, FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import FileManagerModal from '../components/FileManagerModal';

const ITEMS_PER_PAGE = 8;

const STATUS_COLORS = {
  Ongoing: 'text-[#47ace2] bg-[#47ace2]/10 border-[#47ace2]/25',
  Completed: 'text-green-600 bg-green-50 border-green-200',
  Upcoming: 'text-amber-600 bg-amber-50 border-amber-200',
};

const STATUS_BTN = {
  Ongoing: 'bg-[#47ace2]/10 text-[#47ace2] border-[#47ace2]/25',
  Completed: 'bg-green-50 text-green-600 border-green-200',
  Upcoming: 'bg-amber-50 text-amber-600 border-amber-200',
};

const CATEGORIES = ['Residential', 'Commercial', 'Villa', 'Mixed Use', 'Industrial'];
const TYPES = ['Apartment', 'Villa', 'Commercial', 'Office', 'Retail', 'Industrial'];
const STATUSES = ['Ongoing', 'Completed', 'Upcoming'];

const inputClass = "w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2] focus:ring-2 focus:ring-[#47ace2]/10 transition-colors";
const textareaClass = inputClass + " resize-none";
const selectClass = "w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope focus:outline-none focus:border-[#47ace2] focus:ring-2 focus:ring-[#47ace2]/10 transition-colors";

const emptyProject = {
  name: '', location: '', city: 'Nagpur', area: '', category: 'Residential',
  type: 'Apartment', status: 'Ongoing', priceRange: '', description: '',
  completionDate: '', reraNumber: '', totalUnits: '', floors: '',
  images: [''], highlights: [''], amenities: [''], configurations: [''],
  floorPlans: [''],
  featured: false,
};

function ArrayField({ label, values, onChange, placeholder }) {
  const add = () => onChange([...values, '']);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(values.map((item, idx) => idx === i ? v : item));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-slate-500 text-xs font-manrope font-medium">{label}</label>
        <button type="button" onClick={add} className="text-[#47ace2] text-xs font-manrope hover:underline flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {values.map((val, i) => (
          <div key={i} className="flex gap-2">
            <input type="text" value={val} onChange={e => update(i, e.target.value)} placeholder={placeholder} className={inputClass} />
            {values.length > 1 && (
              <button type="button" onClick={() => remove(i)} className="p-2 text-slate-300 hover:text-red-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        ...emptyProject,
        ...initial,
        priceRange: initial.priceRange !== undefined ? initial.priceRange : (initial.price_range !== undefined ? initial.price_range : ''),
        completionDate: initial.completionDate !== undefined ? initial.completionDate : (initial.completion_date !== undefined ? initial.completion_date : ''),
        reraNumber: initial.reraNumber !== undefined ? initial.reraNumber : (initial.rera_number !== undefined ? initial.rera_number : ''),
        totalUnits: initial.totalUnits !== undefined ? initial.totalUnits : (initial.total_units !== undefined ? initial.total_units : ''),
        floorPlans: initial.floorPlans && initial.floorPlans.length > 0 ? initial.floorPlans : [''],
      };
    }
    return { ...emptyProject };
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');
  const [browseIndex, setBrowseIndex] = useState(null);
  const [browseFloorIndex, setBrowseFloorIndex] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.location) { toast.error('Name and location are required'); return; }
    setSaving(true);
    try {
      await onSave({ ...form, totalUnits: Number(form.totalUnits), floors: Number(form.floors) });
      onClose();
      toast.success(initial ? 'Project updated!' : 'Project added!');
    } catch (err) {
      toast.error(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'details', label: 'Details' },
    { id: 'media', label: 'Media & Highlights' },
  ];

  return (
    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-slate-700 font-semibold font-outfit">{initial ? 'Edit Project' : 'Add New Project'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1 px-6 pt-4 flex-shrink-0">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-manrope transition-colors ${
                tab === t.id ? 'bg-[#47ace2] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {tab === 'basic' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Project Name *</label>
                    <input type="text" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Modern Arch Heights" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Category</label>
                    <select value={form.category} onChange={e => set('category', e.target.value)} className={selectClass}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Type</label>
                    <select value={form.type} onChange={e => set('type', e.target.value)} className={selectClass}>
                      {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Status</label>
                    <select value={form.status} onChange={e => set('status', e.target.value)} className={selectClass}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Price Range</label>
                    <input type="text" value={form.priceRange} onChange={e => set('priceRange', e.target.value)} placeholder="₹75 Lakhs - ₹1.2 Crore" className={inputClass} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Location *</label>
                    <input type="text" required value={form.location} onChange={e => set('location', e.target.value)} placeholder="Dharampeth, Nagpur" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Area</label>
                    <input type="text" value={form.area} onChange={e => set('area', e.target.value)} placeholder="Dharampeth" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">City</label>
                    <input type="text" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Nagpur" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Description</label>
                  <textarea rows={4} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Project description..." className={textareaClass} />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div
                    onClick={() => set('featured', !form.featured)}
                    className={`w-5 h-5 rounded flex items-center justify-center border transition-colors cursor-pointer ${form.featured ? 'bg-amber-400 border-amber-400' : 'border-gray-300 bg-white'}`}
                  >
                    {form.featured && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-slate-600 text-sm font-manrope">Featured Project</span>
                </label>
              </>
            )}
            {tab === 'details' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Total Units</label>
                    <input type="number" value={form.totalUnits} onChange={e => set('totalUnits', e.target.value)} placeholder="120" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Floors</label>
                    <input type="number" value={form.floors} onChange={e => set('floors', e.target.value)} placeholder="18" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Completion Date</label>
                    <input type="text" value={form.completionDate} onChange={e => set('completionDate', e.target.value)} placeholder="December 2025" className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">RERA Number</label>
                    <input type="text" value={form.reraNumber} onChange={e => set('reraNumber', e.target.value)} placeholder="P52100045678" className={inputClass} />
                  </div>
                </div>
                <ArrayField label="Configurations (BHK options)" values={form.configurations || ['']} onChange={v => set('configurations', v)} placeholder="e.g. 2 BHK - 1200 sq.ft" />
                <ArrayField label="Amenities" values={form.amenities || ['']} onChange={v => set('amenities', v)} placeholder="e.g. Swimming Pool" />
              </>
            )}
            {tab === 'media' && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-slate-500 text-xs font-manrope font-medium">Project Images *</label>
                    <button
                      type="button"
                      onClick={() => set('images', [...(form.images || []), ''])}
                      className="text-[#47ace2] text-xs font-manrope hover:underline flex items-center gap-1 font-semibold"
                    >
                      <Plus className="w-3 h-3" /> Add Image Slot
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(form.images || ['']).map((val, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            readOnly
                            placeholder="Click 'Browse' to select an image from media library"
                            value={val}
                            onClick={() => setBrowseIndex(i)}
                            className={`${inputClass} cursor-pointer pr-10`}
                          />
                          {val && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-green-500 text-white flex items-center justify-center">
                              <Check className="w-2.5 h-2.5" />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setBrowseIndex(i)}
                          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-manrope text-sm font-medium transition-colors flex items-center gap-1.5"
                        >
                          <FolderOpen className="w-4 h-4 text-slate-500" />
                          Browse
                        </button>
                        {(form.images || ['']).length > 1 && (
                          <button
                            type="button"
                            onClick={() => set('images', form.images.filter((_, idx) => idx !== i))}
                            className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-slate-500 text-xs font-manrope font-medium">Floor Plans</label>
                    <button
                      type="button"
                      onClick={() => set('floorPlans', [...(form.floorPlans || []), ''])}
                      className="text-[#47ace2] text-xs font-manrope hover:underline flex items-center gap-1 font-semibold"
                    >
                      <Plus className="w-3 h-3" /> Add Floor Plan Slot
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(form.floorPlans || ['']).map((val, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            readOnly
                            placeholder="Click 'Browse' to select a floor plan image"
                            value={val}
                            onClick={() => setBrowseFloorIndex(i)}
                            className={`${inputClass} cursor-pointer pr-10`}
                          />
                          {val && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-green-500 text-white flex items-center justify-center">
                              <Check className="w-2.5 h-2.5" />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setBrowseFloorIndex(i)}
                          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-manrope text-sm font-medium transition-colors flex items-center gap-1.5"
                        >
                          <FolderOpen className="w-4 h-4 text-slate-500" />
                          Browse
                        </button>
                        {(form.floorPlans || ['']).length > 1 && (
                          <button
                            type="button"
                            onClick={() => set('floorPlans', form.floorPlans.filter((_, idx) => idx !== i))}
                            className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <ArrayField label="Highlights" values={form.highlights || ['']} onChange={v => set('highlights', v)} placeholder="e.g. Prime Location" />
                <div className="grid grid-cols-2 gap-4">
                  {form.images?.some(u => u) && (
                    <div>
                      <label className="block text-slate-500 text-xs font-manrope font-medium mb-2">Image Previews</label>
                      <div className="grid grid-cols-3 gap-3">
                        {form.images.filter(u => u).map((url, i) => (
                          <img key={i} src={url} alt="" className="h-24 w-full rounded-xl object-cover border border-gray-200" onError={e => { e.target.style.display = 'none'; }} />
                        ))}
                      </div>
                    </div>
                  )}
                  {form.floorPlans?.some(u => u) && (
                    <div>
                      <label className="block text-slate-500 text-xs font-manrope font-medium mb-2">Floor Plan Previews</label>
                      <div className="grid grid-cols-3 gap-3">
                        {form.floorPlans.filter(u => u).map((url, i) => (
                          <img key={i} src={url} alt="" className="h-24 w-full rounded-xl object-cover border border-gray-200" onError={e => { e.target.style.display = 'none'; }} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3 px-6 pb-6 flex-shrink-0">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-slate-500 hover:bg-gray-50 font-manrope text-sm transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-xl bg-[#47ace2] hover:bg-[#1f7db3] text-white font-manrope text-sm font-medium transition-colors disabled:opacity-60 shadow-sm">
              {saving ? 'Saving...' : (initial ? 'Update Project' : 'Add Project')}
            </button>
          </div>
        </form>
      </div>

      {browseIndex !== null && (
        <FileManagerModal
          activeUrl={form.images[browseIndex]}
          onClose={() => setBrowseIndex(null)}
          onSelect={(url) => {
            const updatedImages = [...(form.images || [''])];
            updatedImages[browseIndex] = url;
            set('images', updatedImages);
            setBrowseIndex(null);
          }}
        />
      )}

      {browseFloorIndex !== null && (
        <FileManagerModal
          activeUrl={form.floorPlans[browseFloorIndex]}
          onClose={() => setBrowseFloorIndex(null)}
          onSelect={(url) => {
            const updatedFloorPlans = [...(form.floorPlans || [''])];
            updatedFloorPlans[browseFloorIndex] = url;
            set('floorPlans', updatedFloorPlans);
            setBrowseFloorIndex(null);
          }}
        />
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const { projects, addProject, updateProject, deleteProject } = useAdminData();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);

  const filtered = projects
    .filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.location.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || p.status === filterStatus;
      const matchCat = filterCategory === 'all' || p.category === filterCategory;
      return matchSearch && matchStatus && matchCat;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'featured') return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      return 0;
    });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Delete "${name}"?`)) {
      try {
        await deleteProject(id);
        toast.success('Project deleted');
      } catch (err) {
        toast.error(err.message || 'Failed to delete project');
      }
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-outfit">Projects</h1>
          <p className="text-slate-400 font-manrope text-sm mt-1">{projects.length} total projects</p>
        </div>
        <button
          onClick={() => setModal('add')}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-xl font-manrope text-sm font-medium transition-colors shadow-md shadow-[#47ace2]/25"
        >
          <Plus className="w-4 h-4" />
          Add Project
        </button>
      </div>

      {/* Status Quick Filters */}
      <div className="flex flex-wrap gap-3">
        {[{ key: 'all', label: 'All Projects', count: projects.length }, ...STATUSES.map(s => ({ key: s, label: s, count: projects.filter(p => p.status === s).length }))].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => { setFilterStatus(key); setPage(1); }}
            className={`px-4 py-2 rounded-xl font-manrope text-sm font-medium transition-all border ${
              filterStatus === key
                ? key === 'all' ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : STATUS_BTN[key] + ' border font-semibold'
                : 'border-gray-200 text-slate-500 hover:border-gray-300 bg-white hover:text-slate-700'
            }`}
          >
            {label} <span className="ml-1.5 text-xs opacity-70">{count}</span>
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-wrap gap-3 items-center shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, location, category..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]"
          />
        </div>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }} className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-600 text-sm font-manrope focus:outline-none">
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-600 text-sm font-manrope focus:outline-none">
          <option value="newest">Newest First</option>
          <option value="name">By Name</option>
          <option value="featured">Featured First</option>
        </select>
      </div>

      {/* Projects Table */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide">Project</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide hidden md:table-cell">Category</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide hidden lg:table-cell">Location</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide hidden xl:table-cell">Price</th>
                <th className="px-5 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-16 text-slate-400 font-manrope">
                  <Building2 className="w-10 h-10 mx-auto mb-3 opacity-25" /><p>No projects found</p>
                </td></tr>
              ) : paginated.map(p => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt="" className="w-10 h-10 rounded-xl object-cover border border-gray-200 flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-[#47ace2]/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-[#47ace2]" />
                        </div>
                      )}
                      <div>
                        <p className="text-slate-700 text-sm font-manrope font-semibold flex items-center gap-1.5">
                          {p.name}
                          {p.featured && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                        </p>
                        <p className="text-slate-400 text-xs font-manrope">{p.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell"><span className="text-slate-600 text-sm font-manrope">{p.category}</span></td>
                  <td className="px-5 py-4 hidden lg:table-cell"><span className="text-slate-500 text-sm font-manrope">{p.location}</span></td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-manrope font-semibold border ${STATUS_COLORS[p.status]}`}>{p.status}</span>
                  </td>
                  <td className="px-5 py-4 hidden xl:table-cell"><span className="text-slate-400 text-xs font-manrope">{p.price_range || p.priceRange || '—'}</span></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setModal(p)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-slate-400 hover:text-slate-600 transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(p.id, p.name)} className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-xl bg-white border border-gray-200 text-slate-400 hover:text-slate-600 disabled:opacity-40 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-xl text-sm font-manrope transition-colors ${p === page ? 'bg-[#47ace2] text-white shadow-sm' : 'bg-white border border-gray-200 text-slate-500 hover:text-slate-700'}`}>{p}</button>
          ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-xl bg-white border border-gray-200 text-slate-400 hover:text-slate-600 disabled:opacity-40 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {modal && (
        <ProjectModal
          initial={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={async (data) => {
            if (modal === 'add') return addProject(data);
            else return updateProject(modal.id, data);
          }}
        />
      )}
    </div>
  );
}
